/**
 * Caja del día (F3-H): banner de tasa BCV + captura inline, cola de verificación
 * pendiente, pagos de hoy y accesos a facturas/tasas. Server-only (REST).
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Banknote, ClipboardCheck, Coins, FileText } from "lucide-solid";
import { Link } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { Skeleton } from "@/components/ui/skeleton";
import { ServerDataGate } from "@/components/ui/server-data-gate";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { Money } from "./money";
import { usePayments, useRates } from "../api";

const dayOf = (iso: string): string => iso.split("T")[0] ?? iso;

export const FinanceDashboard: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const rates = useRates(activeOrgId);
  const allPayments = usePayments(activeOrgId, () => undefined);

  const latestRate = () => rates.data?.[0];
  const today = dayOf(new Date().toISOString());
  const todaysPayments = createMemo(() =>
    (allPayments.data ?? []).filter((p) => dayOf(p.createdAt) === today),
  );
  const pendingCount = createMemo(
    () => (allPayments.data ?? []).filter((p) => p.status === "pending_verification").length,
  );
  const todayTotalUsdMinor = createMemo(() =>
    todaysPayments().reduce((sum, p) => sum + (p.status === "rejected" ? 0 : p.amountUsdMinor), 0),
  );

  return (
    // LF-B: la caja es server-only — sin red muestra un estado offline honesto
    // (antes: skeleton eterno con la query pending+paused).
    <ServerDataGate
      pending={allPayments.isLoading}
      error={allPayments.isError}
      refetch={() => void allPayments.refetch()}
      skeleton={<Skeleton class="h-64 w-full rounded-2xl" />}
    >
      <div class="space-y-4">
        <div class="grid gap-3 sm:grid-cols-3">
          {/* Tasa del día */}
          <div class="rounded-xl border border-border p-4" data-testid="rate-banner">
            <div class="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Coins class="h-4 w-4" />
              {t(msg`BCV rate`)}
            </div>
            <Show
              when={latestRate()}
              fallback={<p class="text-sm text-muted-foreground">{t(msg`No rate set`)}</p>}
            >
              {(r) => (
                <p class="text-2xl font-bold tabular-nums">
                  Bs. {r().vesPerUsd.toFixed(2)}
                  <span class="ml-1 text-xs font-normal text-muted-foreground">/ USD</span>
                </p>
              )}
            </Show>
            <Link to="/finance/rates" class="text-xs text-primary hover:underline">
              {t(msg`Manage rates`)}
            </Link>
          </div>

          {/* Cola de verificación */}
          <Link
            to="/admin/billing"
            class="rounded-xl border border-border p-4 transition-colors hover:bg-secondary/10"
            data-testid="pending-queue"
          >
            <div class="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
              <ClipboardCheck class="h-4 w-4" />
              {t(msg`Pending verification`)}
            </div>
            <p class="text-2xl font-bold tabular-nums">{pendingCount()}</p>
          </Link>

          {/* Total de hoy */}
          <div class="rounded-xl border border-border p-4">
            <div class="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Banknote class="h-4 w-4" />
              {t(msg`Collected today`)}
            </div>
            <p class="text-2xl font-bold">
              <Money
                usdMinor={todayTotalUsdMinor()}
                vesPerUsd={latestRate()?.vesPerUsd}
                showRate={false}
              />
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <Link
            to="/finance/payments"
            class="inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            <Banknote class="h-4 w-4" />
            {t(msg`Record payment`)}
          </Link>
          <Link
            to="/finance/invoices"
            class="inline-flex items-center gap-1 rounded-lg border border-border px-4 py-2 text-sm"
          >
            <FileText class="h-4 w-4" />
            {t(msg`Invoices`)}
          </Link>
          {/* AUTONOMY: config de cobros alcanzable por org_admin (métodos/planes/
              descuentos/recibo). El server aplica ManageBilling ∥ org admin. */}
          <Link
            to="/finance/settings"
            data-testid="finance-settings-link"
            class="inline-flex items-center gap-1 rounded-lg border border-border px-4 py-2 text-sm"
          >
            {t(msg`Billing configuration`)}
          </Link>
        </div>

        <div class="rounded-xl border border-border p-4">
          <h3 class="mb-2 font-semibold">{t(msg`Today's payments`)}</h3>
          <Show when={!allPayments.isLoading} fallback={<Skeleton class="h-20 w-full" />}>
            <Show
              when={todaysPayments().length > 0}
              fallback={
                <p class="text-sm text-muted-foreground">{t(msg`No payments yet today`)}</p>
              }
            >
              <ul class="divide-y divide-border" data-testid="today-payments">
                <For each={todaysPayments()}>
                  {(p) => (
                    <li class="flex items-center justify-between py-2 text-sm">
                      <span class="truncate">{p.studentUserId}</span>
                      <Money
                        usdMinor={p.amountUsdMinor}
                        vesPerUsd={latestRate()?.vesPerUsd}
                        showRate={false}
                      />
                      <span class="text-xs text-muted-foreground">{p.status}</span>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </Show>
        </div>
      </div>
    </ServerDataGate>
  );
};
